import { Activity } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { DocumentSpec } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { DocumentModel, DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { entityInstancePathFromA12Path } from "../../utilities/pathHelpers";

const documentService = new DocumentServiceFactory().getDocumentService();

/** Werte des Feldes `zahlungsstatus`, siehe `EpayblZahlungsvorgangAccessor` im Portal-Backend. */
export const ZAHLUNGSSTATUS_WARTEND = "wartend";
export const ZAHLUNGSSTATUS_BEZAHLT = "bezahlt";

export function normalizeA12Path(pathWithPrefix?: string): string | undefined {
	if (!pathWithPrefix) {
		return undefined;
	}
	const rootStart = pathWithPrefix.indexOf("/root/");
	return rootStart >= 0 ? pathWithPrefix.substring(rootStart) : undefined;
}

export function getTypedValue<T>(
	document: Activity.Data.Document,
	path: string,
	guard: (value: unknown) => value is T
): T | undefined {
	const value = documentService.getAssignedObject(document, entityInstancePathFromA12Path(path));
	return guard(value) ? value : undefined;
}

export function getStringValue(document: Activity.Data.Document, path: string): string | undefined {
	return getTypedValue(document, path, (value): value is string => typeof value === "string");
}

export function getNumberValue(document: Activity.Data.Document, path: string): number | undefined {
	return getTypedValue(document, path, (value): value is number => typeof value === "number");
}

export function getBooleanValue(document: Activity.Data.Document, path: string): boolean | undefined {
	return getTypedValue(document, path, (value): value is boolean => typeof value === "boolean");
}

/**
 * Baut das aktualisierte {@link Activity.Data.Document} aus dem vom Server zurückgegebenen
 * {@link DocumentSpec} auf (Datumswerte parsen, {@code modelId}/{@code id} ergänzen). Wird nach
 * jedem der ePayment-RPC-Aufrufe (Anlegen/Statusabfrage) identisch benötigt.
 */
export function buildUpdatedDocument(
	documentSpec: DocumentSpec,
	documentModel: DocumentModel,
	documentId: string
): Activity.Data.Document {
	return {
		...documentService.parseDates(documentSpec.document, documentModel),
		modelId: documentModel.header.id,
		id: documentId
	};
}

/**
 * Entfernt {@code modelId} aus dem Dokument und formatiert dessen Datumswerte für die
 * Übertragung an den Server (Gegenstück zu {@link buildUpdatedDocument}).
 *
 * <p>{@code modelId} ist nur ein Client-seitiges Bookkeeping-Feld und kein Bestandteil des
 * {@code DocumentModel}. Bliebe es im JSON, schlägt die serverseitige Validierung fehl: "The
 * validation of document of document model '…' failed. For the entity instance 'modelId', the
 * corresponding entity was not found in the corresponding document model."
 */
export function formatDocumentForRequest(document: Activity.Data.Document, documentModel: DocumentModel): object {
	const documentWithoutModelId = { ...document };
	Reflect.deleteProperty(documentWithoutModelId, "modelId");
	return documentService.formatDates(documentWithoutModelId, documentModel);
}
